// Perspective orbit controller with NO fixed up — so you can keep rotating over
// the poles (trackball, like Blender) instead of locking at straight up/down.
// RMB rotates, MMB pans, wheel zooms; LMB is left for the tool. Rotation/pan/
// zoom are applied directly on input (no spherical state, no clamp), so it never
// fights an externally-moved camera (e.g. the corner-gizmo view-align tween).
//
// Drop-in for the bits of OrbitControls the app used: `target`, `enabled`,
// `minDistance`, `maxDistance`, `update()`.

import * as THREE from "three";

import { elementRect } from "../input/element-rect";

export class Orbit3D {
  readonly target = new THREE.Vector3();
  enabled = true;
  // When false, RMB no longer free-orbits (used by the model's orthographic
  // view, where right-drag instead snaps between axis-aligned views). Pan + zoom
  // stay available.
  rotateEnabled = true;
  minDistance = 0.1;
  maxDistance = 1000;
  rotateSpeed = (2 * Math.PI) / 700; // radians per pixel
  zoomSpeed = 0.0015;
  // Optional pan-scale reference: returns the world distance to whatever the
  // cursor is over (so panning tracks that surface 1:1), or null to fall back to
  // the camera→target distance. Captured at pan-start, used for the gesture.
  // Without it, zooming in close to the orbit pivot collapses the pivot distance
  // and panning the (farther) model surface feels sluggish.
  panDistance?: () => number | null;
  // Optional orbit-pivot pick: returns the world point under the cursor (a
  // voxel/surface hit, or the floor plane), or null over empty space. Captured
  // at RMB-down to re-peg the pivot DEPTH onto that surface — WASD flight slides
  // the pivot along rigidly, so after flying toward a wall the pivot floats far
  // behind it and a right-drag swings the view wildly. We only adopt the depth
  // along the current view axis (not the lateral offset): the turntable always
  // looks AT the pivot, so moving it off-axis would snap the view. Same axis,
  // corrected distance → no snap, and the surface you're looking at stays put.
  pivotPick?: () => THREE.Vector3 | null;
  // When true, the pitch is clamped to [minElev, maxElev] and the up stays +Y,
  // so the view can never tumble over a pole to the upside-down side. The floor
  // 3D view uses a sliver-above-horizon..near-overhead band (so you can't dip
  // under the floor); model mode uses a symmetric band so you can look from
  // above OR below but still can't flip past the poles. False = free tumbling.
  clampGround = false;
  // Pitch limits used while clampGround is on (radians). Defaults to the floor
  // band; model mode widens minElev to allow looking up from below.
  minElev = (3 * Math.PI) / 180;
  maxElev = (89 * Math.PI) / 180;

  private cam: THREE.PerspectiveCamera;
  private dom: HTMLElement;
  private mode: "none" | "rotate" | "pan" = "none";
  private lastX = 0;
  private lastY = 0;

  private _off = new THREE.Vector3();
  private _ax = new THREE.Vector3();
  private _fwd = new THREE.Vector3();
  private _q = new THREE.Quaternion();
  private _px = new THREE.Vector3();
  private _py = new THREE.Vector3();
  private _panRefDist = 1; // pan-scale distance, captured at pan-start

  constructor(cam: THREE.PerspectiveCamera, dom: HTMLElement) {
    this.cam = cam;
    this.dom = dom;
    dom.addEventListener("contextmenu", (e) => e.preventDefault());
    dom.addEventListener("pointerdown", this.onDown);
    window.addEventListener("pointermove", this.onMove);
    window.addEventListener("pointerup", this.onUp);
    dom.addEventListener("wheel", this.onWheel, { passive: false });
  }

  private onDown = (e: PointerEvent): void => {
    if (!this.enabled) return;
    if (e.button === 2) {
      if (!this.rotateEnabled) return; // RMB freed for the axis-snap swipe
      this.mode = "rotate";
      // Re-peg the pivot depth onto the surface under the cursor (see pivotPick).
      // Keep the pivot on the current view axis — only its distance changes — so
      // the turntable's lookAt(target) stays a no-op and the view doesn't snap.
      const p = this.pivotPick?.();
      if (p) {
        this.cam.getWorldDirection(this._fwd).normalize();
        const d = this._off.copy(p).sub(this.cam.position).dot(this._fwd);
        if (d > this.minDistance) {
          this.target.copy(this.cam.position).addScaledVector(this._fwd, d);
        }
      }
    } else if (e.button === 1) {
      this.mode = "pan"; // MMB
      // Calibrate pan to whatever's under the cursor (falls back to the pivot
      // distance over empty space), so it tracks the surface at any zoom.
      this._panRefDist =
        this.panDistance?.() ?? this.cam.position.distanceTo(this.target);
    } else return; // LMB → tool
    this.lastX = e.clientX;
    this.lastY = e.clientY;
  };
  private onMove = (e: PointerEvent): void => {
    if (this.mode === "none") return;
    const dx = e.clientX - this.lastX;
    const dy = e.clientY - this.lastY;
    this.lastX = e.clientX;
    this.lastY = e.clientY;
    if (this.mode === "rotate") this.rotateByPixels(dx, dy);
    else this.pan(dx, dy);
  };
  private onUp = (e: PointerEvent): void => {
    if (
      (e.button === 2 && this.mode === "rotate") ||
      (e.button === 1 && this.mode === "pan")
    ) {
      this.mode = "none";
    }
  };
  private onWheel = (e: WheelEvent): void => {
    if (!this.enabled) return;
    e.preventDefault();
    this._off.copy(this.cam.position).sub(this.target);
    let dist = this._off.length();
    dist = Math.max(
      this.minDistance,
      Math.min(this.maxDistance, dist * (1 + e.deltaY * this.zoomSpeed)),
    );
    this._off.setLength(dist);
    this.cam.position.copy(this.target).add(this._off);
  };

  // Turntable rotate by a pixel delta (Blender-default style). Yaw is ALWAYS
  // about the world up axis, so the horizon stays level and "up stays up" — no
  // roll ever accumulates. Pitch is about the horizontal right axis (⊥ to view
  // and up) and is NOT clamped, so the view still tumbles over the poles. The
  // tracked up follows both rotations, so it crosses the pole smoothly (the
  // world goes upside-down, exactly like Blender). Also drives the gizmo drag.
  rotateByPixels(dx: number, dy: number): void {
    this._off.copy(this.cam.position).sub(this.target);
    // Floor 3D: a strict elevation-clamped turntable. Reconstruct the offset
    // from (azimuth, clamped elevation) each step so the orbit can never reach
    // the pole — rotating the free trackball PAST it and rescaling a near-zero
    // horizontal component is what made the walls jitter overhead.
    if (this.clampGround) {
      const len = this._off.length() || 1;
      const horiz = Math.hypot(this._off.x, this._off.z);
      // Heading (azimuth) of the camera around the target. Looking straight down
      // the pole the horizontal offset is ~zero, so the heading is undefined —
      // derive it from the up vector instead (its horizontal direction is the
      // meridian that keeps "up" at the top of the screen). Without this, the
      // first drag out of a straight-down view snaps to an arbitrary side. The
      // on-screen up relates to the camera's heading with OPPOSITE sign above vs.
      // below the target (the meridian points away from the eye looking down, but
      // toward it looking up), so the bottom pole flips the sign — otherwise the
      // first drag out of an underside view spins 180° around Y.
      const azBase =
        horiz > 1e-4
          ? Math.atan2(this._off.x, this._off.z)
          : this._off.y >= 0
            ? Math.atan2(-this.cam.up.x, -this.cam.up.z)
            : Math.atan2(this.cam.up.x, this.cam.up.z);
      const az = azBase - dx * this.rotateSpeed;
      const el = Math.min(
        this.maxElev,
        Math.max(this.minElev, Math.atan2(this._off.y, horiz) + dy * this.rotateSpeed),
      );
      const ch = Math.cos(el) * len;
      this._off.set(Math.sin(az) * ch, Math.sin(el) * len, Math.cos(az) * ch);
      this.cam.up.set(0, 1, 0);
      this.cam.position.copy(this.target).add(this._off);
      this.cam.lookAt(this.target);
      return;
    }
    const up = this.cam.up;
    // Yaw about the vertical axis — keeps the orbit axis vertical, so no horizon
    // drift. Flip it when upside-down (up points below the horizon) so a
    // horizontal drag still tracks the screen instead of reversing.
    this._ax.set(0, up.y < 0 ? -1 : 1, 0);
    this._q.setFromAxisAngle(this._ax, -dx * this.rotateSpeed);
    this._off.applyQuaternion(this._q);
    up.applyQuaternion(this._q);
    // Pitch about right = forward × up (stays horizontal → still no roll).
    this._fwd.copy(this._off).multiplyScalar(-1).normalize();
    this._ax.crossVectors(this._fwd, up).normalize();
    this._q.setFromAxisAngle(this._ax, -dy * this.rotateSpeed);
    this._off.applyQuaternion(this._q);
    up.applyQuaternion(this._q);
    this.cam.position.copy(this.target).add(this._off);
    this.cam.lookAt(this.target);
  }

  private pan(dx: number, dy: number): void {
    const perPx =
      (2 * Math.tan((this.cam.fov * Math.PI) / 360) * this._panRefDist) /
      Math.max(1, elementRect(this.dom).height);
    this.cam.updateMatrixWorld();
    this._px.setFromMatrixColumn(this.cam.matrixWorld, 0).multiplyScalar(-dx * perPx);
    this._py.setFromMatrixColumn(this.cam.matrixWorld, 1).multiplyScalar(dy * perPx);
    this.target.add(this._px).add(this._py);
    this.cam.position.add(this._px).add(this._py);
  }

  update(): void {
    // Rotation/pan/zoom are applied on input; just keep the camera aimed (in
    // case the target was moved externally, e.g. framing).
    this.cam.lookAt(this.target);
  }
}
